import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { EnvironmentManager } from '../config/EnvironmentManager';
import { getUserTimeZone } from '../utils/timezone';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

const SavedJobsContext = createContext();

export const useSavedJobs = () => {
  const context = useContext(SavedJobsContext);
  if (!context) {
    throw new Error('useSavedJobs must be used within a SavedJobsProvider');
  }
  return context;
};

const getIdToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) return await user.getIdToken(true);
  return null;
};

export const SavedJobsProvider = ({ children }) => {
  const { currentUser, userId, loading: authLoading } = useAuth();

  // savedJobIds: Set of jobPostingId strings
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  // savedJobsData: full list of saved job objects from backend
  const [savedJobsData, setSavedJobsData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);

  // Fetch all saved jobs once user is authenticated
  const fetchSavedJobs = useCallback(async () => {
    if (!userId) return;
    setFetchLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const zoneId = getUserTimeZone();
      const url = `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getSavedJobByUser}/${userId}?zoneId=${encodeURIComponent(zoneId)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const jobsArray = Array.isArray(data) ? data : (data.savedJobs || data.jobs || []);
        setSavedJobsData(jobsArray);

        // Extract job IDs
        const ids = new Set(
          jobsArray.map(item => {
            const job = item.job || item;
            return String(job.jobPostingId || job.id || item.jobPostingId || item.id || '');
          }).filter(Boolean)
        );
        setSavedJobIds(ids);
      }
    } catch (err) {
      console.error('SavedJobsContext: Error fetching saved jobs:', err);
    } finally {
      setFetchLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      setSavedJobIds(new Set());
      setSavedJobsData([]);
      return;
    }
    fetchSavedJobs();
  }, [authLoading, currentUser, fetchSavedJobs]);

  // Save a job — returns true on success
  // Save a job — returns true on success
const saveJob = useCallback(async (jobPostingId) => {
  if (!currentUser || !userId) return false;

  try {
    const token = await getIdToken();

    const zoneId = getUserTimeZone();

    const url =
      `${EnvironmentManager.baseUrl}` +
      `${EnvironmentManager.endpoints.saveJob}` +
      `/${userId}/${jobPostingId}?zoneId=${encodeURIComponent(zoneId)}`;

    console.log("Saving job URL:", url);

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (res.ok) {
      const savedJob = await res.json();

      setSavedJobIds(prev =>
        new Set([...prev, String(jobPostingId)])
      );

      setSavedJobsData(prev => [
        ...prev,
        savedJob
      ]);

      toast.success('Job saved successfully!');
      return true;
    }

    const errorText = await res.text();
    console.error("Save job failed:", errorText);
    toast.error('Failed to save job');

    return false;

  } catch (err) {
    console.error('SavedJobsContext: Error saving job:', err);
    toast.error('An unexpected error occurred');
    return false;
  }

}, [currentUser, userId]);

  // Remove a saved job — returns true on success
  const removeSavedJob = useCallback(async (jobPostingId) => {
    if (!currentUser) return false;
    try {
      const token = await getIdToken();
      const zoneId = getUserTimeZone();
      const url = `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.deleteSavedJob}/${userId}/${jobPostingId}?zoneId=${encodeURIComponent(zoneId)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        // Update ID set immediately
        setSavedJobIds(prev => {
          const next = new Set(prev);
          next.delete(String(jobPostingId));
          return next;
        });
        // Update full data list immediately
        setSavedJobsData(prev =>
          prev.filter(item => {
            const job = item.job || item;
            const id = String(job.jobPostingId || job.id || item.jobPostingId || item.id || '');
            return id !== String(jobPostingId);
          })
        );
        toast.success('Job removed from saved list');
        return true;
      }
      toast.error('Failed to remove job');
      return false;
    } catch (err) {
      console.error('SavedJobsContext: Error removing saved job:', err);
      toast.error('An unexpected error occurred');
      return false;
    }
  }, [currentUser, userId]);

  const isJobSaved = useCallback((jobPostingId) => {
    return savedJobIds.has(String(jobPostingId));
  }, [savedJobIds]);

  const value = {
    savedJobIds,
    savedJobsData,
    fetchLoading,
    fetchSavedJobs,
    saveJob,
    removeSavedJob,
    isJobSaved,
  };

  return (
    <SavedJobsContext.Provider value={value}>
      {children}
    </SavedJobsContext.Provider>
  );
};

export default SavedJobsContext;
